/**
 * Alerts Banner
 *
 * Dismissible alert banner for analytics anomalies.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import type { FunnelAlert } from '../../../types/analytics';

interface AlertsBannerProps {
  alerts: FunnelAlert[];
}

export const AlertsBanner: React.FC<AlertsBannerProps> = ({ alerts }) => {
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  const visibleAlerts = alerts.filter((_, i) => !dismissed.has(i));

  if (visibleAlerts.length === 0) return null;

  return (
    <div className="space-y-2">
      <AnimatePresence>
        {alerts.map((alert, idx) => {
          if (dismissed.has(idx)) return null;

          const isCritical = alert.severity === 'critical';

          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`flex items-start gap-3 p-3 rounded-xl border ${
                isCritical
                  ? 'bg-red-50 border-red-200'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <AlertTriangle
                className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  isCritical ? 'text-red-500' : 'text-amber-500'
                }`}
              />
              <p
                className={`text-xs flex-1 ${
                  isCritical ? 'text-red-700' : 'text-amber-700'
                }`}
              >
                {alert.message}
              </p>
              <button
                onClick={() => setDismissed((s) => new Set(s).add(idx))}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
